import WebSDK, {SDK} from '@ua/web-sdk'

import createDefaultWebSdkStorage, {WebSdkStorage} from './storage'
import createDefaultUserMetaClient, {UserMetaClient} from './user-meta'

enum Groups {
  Device = 'device',
}

export type Group = `${Groups}`

type ValidTagMapping = {[key in Group]: string[]}

interface ValidTags extends ValidTagMapping {
  ['device']: []
}

export interface WebSdkWrapper {
  addDeviceTags<T extends Group>(
    group: T,
    ...tags: ValidTags[T][number][]
  ): Promise<void>
  addContactTags<T extends Group>(
    group: T,
    ...tags: ValidTags[T][number][]
  ): Promise<void>
  /**
   * Track an event as a custom event. This method is public because it will be
   * called from the `track` module, but should not be called directly and so
   * has a `_` prefixed name.
   * @private
   * @param action the action that occurred
   * @param context the context in which it occurred
   * @param description description of the action
   * @param properties any additional properties that go along with the action
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _track(name: string, properties: Record<string, unknown>): Promise<void>
}

class WebSdkWrapperImpl implements WebSdkWrapper {
  private _sdk: SDK
  private _client: UserMetaClient
  private _storage: WebSdkStorage

  constructor(sdk: SDK, client: UserMetaClient, storage: WebSdkStorage) {
    this._sdk = sdk
    this._client = client
    this._storage = storage
  }

  async init(): Promise<void> {
    const tasks: Promise<unknown>[] = []
    const channelId = await this._storage.getChannelId()
    const namedUserId = await this._storage.getNamedUserId()

    const meta = await this._client.get()
    if (!channelId) {
      // we want to ensure the channel is created before doing anything else
      const channel = await this._sdk.create()
      tasks.push(this._storage.setChannelId(channel.id))
      tasks.push(channel.tags.add(meta.environment))
    }
    // set the named user id if it doesn't match what we have stored, but don't
    // unset it if not provided by the backend
    if (meta.named_user_id && meta.named_user_id !== namedUserId) {
      const contact = await this._sdk.contact
      const id = meta.named_user_id
      tasks.push(
        contact.identify(id).then(() => this._storage.setNamedUserId(id))
      )
    }

    await Promise.all(tasks)
  }

  async addDeviceTags(): Promise<void> {
    if (!this._sdk.channel) {
      throw new Error('no channel available')
    }
    // TODO(TPS-4030)
    throw new Error('not implemented')
  }

  async addContactTags(): Promise<void> {
    // TODO(TPS-4030)
    throw new Error('not implemented')
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  async _track(
    name: string,
    properties: Record<string, unknown>
  ): Promise<void> {
    const event = new this._sdk.CustomEvent(name, undefined, properties)

    const result = await event.track()
    if (!result.ok) {
      throw new Error(
        `failed submitting event: ${result.message ?? 'unknown error'}`
      )
    }
  }
}

// the singleton wrapper is stored here
let defaultSdk: Promise<WebSdkWrapper> | null

async function initDefaultSdk(): Promise<WebSdkWrapperImpl> {
  const sdk = await WebSDK
  const client = createDefaultUserMetaClient()
  const storage = createDefaultWebSdkStorage()

  const wrapper = new WebSdkWrapperImpl(sdk, client, storage)
  await wrapper.init()

  return wrapper
}

/**
 * Returns a singleton instance of the Web SDK wrapper, which provides an
 * interface for the Airship Web SDK. If the singleton instance isn't
 * initialized yet, it is created.
 *
 * When initialized, the SDK will register a channel for the current browser,
 * and associate it to a stable Named User ID for the user.
 * @returns a singleton web sdk wrapper
 */
export default async function getDefaultSdk(): Promise<WebSdkWrapper> {
  if (defaultSdk) {
    return defaultSdk
  }

  defaultSdk = initDefaultSdk()

  return defaultSdk
}

/**
 * EXPORTED FOR TESTING ONLY. Creates a web sdk wrapper with the provided
 * dependencies.
 *
 * @private
 * @param sdk the web sdk global
 * @param client user metadata client
 * @param storage web sdk storage
 * @returns a web sdk wrapper
 */
export async function createSdk(
  sdk: SDK,
  client: UserMetaClient,
  storage: WebSdkStorage
): Promise<WebSdkWrapper> {
  const wrapper = new WebSdkWrapperImpl(sdk, client, storage)
  await wrapper.init()

  return wrapper
}
